import React, { useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import { FiLoader } from 'react-icons/fi';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

const BudgetTemplateModal = ({ 
  onClose, 
  onSubmit, 
  editingTemplate, 
  categories,
  updateCategories,
  currencies 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: editingTemplate?.title || '',
    description: editingTemplate?.description || '',
    categories: editingTemplate?.categories || []
  });

  const categoryOptions = categories
    .map(cat => ({ value: cat, label: cat }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const currencyOptions = currencies.map(curr => ({ 
    value: curr, 
    label: `${curr} (${CURRENCY_SYMBOLS[curr]})`
  }));

  const handleCreateCategory = async (inputValue) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const newCategories = [...categories, newCategory];
        await updateCategories(newCategories);
        toast.success('Category created successfully');
      } catch (error) {
        toast.error('Failed to create category');
      }
    }
  };

  const handleAddCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [
        ...prev.categories,
        { 
          category: '', 
          amount: '', 
          currency: currencies[0] || 'GBP' // Ensure default currency
        }
      ]
    }));
  };

  const handleRemoveCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

  const handleCategoryChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) => 
        i === index ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const handleAmountChange = (index, e) => {
    let value = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    handleCategoryChange(index, 'amount', value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    if (!formData.title.trim()) {
      toast.error('Template title is required');
      return;
    }

    if (formData.categories.length === 0) {
      toast.error('At least one category is required');
      return;
    }

    const invalidCategories = formData.categories.filter(
      cat => !cat.category || !cat.amount || !cat.currency
    );

    if (invalidCategories.length > 0) {
      toast.error('All category fields are required');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        ...formData,
        id: editingTemplate?.id,
        title: formData.title.trim(),
        description: formData.description.trim()
      });
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setIsLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      background: 'var(--input-bg)',
      borderColor: state.isFocused ? '#6366f1' : 'var(--input-border)',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : null,
      '&:hover': {
        borderColor: '#6366f1'
      }
    }),
    menu: (base) => ({
      ...base,
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected 
        ? '#6366f1' 
        : state.isFocused 
          ? 'var(--hover-bg)' 
          : 'transparent',
      color: state.isSelected ? 'white' : 'var(--text-primary)',
    }),
    singleValue: (base) => ({
      ...base,
      color: 'var(--text-primary)'
    }),
    input: (base) => ({
      ...base,
      color: 'var(--text-primary)'
    }),
    placeholder: (base) => ({
      ...base,
      color: 'var(--text-tertiary)'
    })
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
              {editingTemplate ? 'Edit Budget Template' : 'Create Budget Template'}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <FaTimes className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title & Description */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl 
                  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors
                  dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter template title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl 
                  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors
                  dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter template description"
                rows={3}
              />
            </div>
          </div>

          {/* Categories Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Category Budgets
              </label>
              <button
                type="button"
                onClick={handleAddCategory}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/50 
                  text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 
                  dark:hover:bg-indigo-900 transition-colors"
              >
                <FaPlus size={12} />
                Add Category
              </button>
            </div>

            <AnimatePresence>
              <div className="space-y-4">
                {formData.categories.map((cat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex gap-4 items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl group"
                  >
                    <div className="flex-1 space-y-3 dark:text-gray-100">
                      <CreatableSelect
                        options={categoryOptions}
                        value={{ value: cat.category, label: cat.category }}
                        onChange={(selected) => handleCategoryChange(index, 'category', selected.value)}
                        onCreateOption={handleCreateCategory}
                        placeholder="Select or create category"
                        styles={customSelectStyles}
                      />
                      <div className="flex items-center gap-2">
                        <Select
                          options={currencyOptions}
                          value={currencyOptions.find(opt => opt.value === cat.currency)}
                          onChange={(selected) => handleCategoryChange(index, 'currency', selected.value)}
                          styles={customSelectStyles}
                          className="w-32"
                        />
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400">
                            {CURRENCY_SYMBOLS[cat.currency]}
                          </span>
                          <input
                            type="number"
                            value={cat.amount}
                            onChange={(e) => handleAmountChange(index, e)}
                            onBlur={(e) => {
                              // Format to 2 decimal places when leaving the input
                              if (e.target.value && !isNaN(e.target.value)) {
                                handleCategoryChange(index, 'amount', Number(e.target.value).toFixed(2));
                              }
                            }}
                            className="w-full pl-8 pr-4 py-2.5 bg-white dark:bg-gray-700 
                              border border-gray-300 dark:border-gray-600 rounded-xl 
                              focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                              transition-colors dark:text-gray-100"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCategory(index)}
                      className="mt-2 p-2 text-red-500 hover:text-red-700 dark:text-red-400 
                        dark:hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaTrash size={16} />
                    </button>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl 
                text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin" />
                  {editingTemplate ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingTemplate ? 'Update Template' : 'Create Template'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default BudgetTemplateModal; 